import { API_BASE } from "../../../../utils/api";
import ForgotPasswordModal from "./ForgotPasswordModal";
const EyeOpen = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
  </svg>
);

const EyeClosed = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const LoginForm = ({
  logo,
  username, setUsername,
  password, setPassword,
  rememberMe, setRememberMe,
  error,
  showPassword, setShowPassword,
  isLoading,
  isForgotPasswordOpen,
  closeForgotPassword,
  handleLogin,
  handleForgotPassword,
}) => (
  <div className="flex-1 flex flex-col justify-center px-10 py-12">
    <ForgotPasswordModal open={isForgotPasswordOpen} onClose={closeForgotPassword} />
    {/* Logo */}
    <div className="flex justify-center mb-8">
      <img
        src={logo ? `${API_BASE}${logo}` : "/img/finglider_logo.jpg"}
        alt=" Logo"
        className="h-14 w-auto"
        onError={(e) => (e.target.src = "/img/finglider_logo.jpg")}
      />
    </div>

    <h1 className="text-2xl font-bold text-black text-center mb-1">Welcome back</h1>
    <p className="text-gray-500 text-sm text-center mb-8">Sign in to your account</p>

    {/* Error banner */}
    {error && (
      <div className="mb-5 p-3 bg-black rounded-xl flex items-center gap-2">
        <svg className="w-4 h-4 text-white shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 3a9 9 0 110 18A9 9 0 0112 3z" />
        </svg>
        <p className="text-white text-sm font-medium">{error}</p>
      </div>
    )}

    {/* Username */}
    <div className="mb-4">
      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">
        Username
      </label>
      <div className="relative">
        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </span>
        <input
          type="text"
          placeholder="Enter your username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleLogin()}
          className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-black placeholder-gray-400 focus:outline-none focus:border-black focus:bg-white transition-all duration-200"
        />
      </div>
    </div>

    {/* Password */}
    <div className="mb-5">
      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">
        Password
      </label>
      <div className="relative">
        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </span>
        <input
          type={showPassword ? "text" : "password"}
          placeholder="Enter your password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleLogin()}
          className="w-full pl-11 pr-12 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-black placeholder-gray-400 focus:outline-none focus:border-black focus:bg-white transition-all duration-200"
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-black transition-colors"
        >
          {showPassword ? <EyeOpen /> : <EyeClosed />}
        </button>
      </div>
    </div>

    {/* Remember me & Forgot password */}
    <div className="flex items-center justify-between mb-7">
      <label className="flex items-center gap-2 cursor-pointer group">
        <div className="relative">
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            className="sr-only"
          />
          <div
            onClick={() => setRememberMe(!rememberMe)}
            className={`w-4 h-4 border-2 rounded transition-all duration-200 flex items-center justify-center cursor-pointer ${rememberMe ? "bg-black border-black" : "bg-white border-gray-300 group-hover:border-gray-500"
              }`}
          >
            {rememberMe && (
              <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
        </div>
        <span className="text-sm text-gray-600 group-hover:text-black transition-colors select-none">
          Remember me
        </span>
      </label>
      <button
        type="button"
        onClick={handleForgotPassword}
        className="text-sm text-gray-500 hover:text-black font-medium transition-colors underline underline-offset-2"
      >
        Forgot password?
      </button>
    </div>

    {/* Sign In button */}
    <button
      onClick={handleLogin}
      disabled={isLoading}
      className="w-full bg-black text-white py-3.5 rounded-xl font-semibold text-sm tracking-wide hover:bg-gray-900 active:scale-[0.98] transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
    >
      {isLoading ? (
        <>
          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          Signing in...
        </>
      ) : (
        <>
          Sign In
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </>
      )}
    </button>
  </div>
);

export default LoginForm;
